import { CarryingHelpExplicitEscape, CarryingHelpServices, ServiceAdvisoryHelpOption } from '@brenger/api-client';
import React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import {
  IconPersonOne,
  IconPersonTwo,
  IconTailgate,
  IconTailgateExtraPerson,
  IconTailgatePalletJack,
  InputTileOption,
} from '../brenger-shared-ui';
import { RootState } from '../types';
import { translate } from '../utils/localization';
import { priceAsString } from '../utils/price';
import { useTypedSelector } from './useTypedSelector';
import { SimplePrice } from '../brenger-shared-ui/components/other/pricedisplay/SimplePrice';

interface OrderedHelpOptions {
  advised: InputTileOption[];
  other: InputTileOption[];
  hasEscape: boolean;
}

type HelpOptionValue = CarryingHelpServices | CarryingHelpExplicitEscape;

export const HELP_ICON_AND_TITLE_TRANSKEY: {
  [key in HelpOptionValue]: { titleKey: string; explanationKey: string; icon: React.ReactNode };
} = {
  needed: {
    titleKey: 'request_flow.pickup.help_carrying.option_2',
    explanationKey: 'request_flow.pickup.help_carrying.option_2_explain',
    icon: <IconPersonOne />,
  },
  extra_driver: {
    titleKey: 'request_flow.pickup.help_carrying.option_3',
    explanationKey: 'request_flow.pickup.help_carrying.option_3_explain',
    icon: <IconPersonTwo />,
  },
  equipment_tailgate: {
    titleKey: 'request_flow.weight_equipment_heavy.equipment_tailgate',
    explanationKey: 'request_flow.weight_equipment_heavy.equipment_tailgate_explain',
    icon: <IconTailgate />,
  },
  equipment_tailgate_extra_driver: {
    titleKey: 'request_flow.weight_equipment_heavy.equipment_tailgate_extra_driver',
    explanationKey: 'request_flow.weight_equipment_heavy.equipment_tailgate_extra_driver_explain',
    icon: <IconTailgateExtraPerson />,
  },
  equipment_tailgate_pallet_jack: {
    titleKey: 'request_flow.weight_equipment_heavy.equipment_tailgate_with_pallet_jack',
    explanationKey: 'request_flow.weight_equipment_heavy.equipment_tailgate_with_pallet_jack_explain',
    icon: <IconTailgatePalletJack />,
  },
  equipment_provided_by_customer: {
    titleKey: 'request_flow.weight_equipment_heavy.equipment_provided_by_customer',
    explanationKey: 'request_flow.weight_equipment_heavy.equipment_provided_by_customer_explain',
    icon: null,
  },
};

export const useServiceAdvisoryHelp = (): OrderedHelpOptions => {
  const service = useTypedSelector((state: RootState) => state.generalTransport.quote?.service_advisory);
  const hasEscape = !!service?.help_options_has_escape;

  const createHelpOption = (option: ServiceAdvisoryHelpOption): InputTileOption => {
    return {
      value: option.help_option,
      title: String(translate(HELP_ICON_AND_TITLE_TRANSKEY[option.help_option].titleKey)),
      desc: <Translate id={HELP_ICON_AND_TITLE_TRANSKEY[option.help_option].explanationKey} />,
      suffix: (
        <span className={'text-primary'}>
          <SimplePrice price={option.price_change} addPlusMinusIndicator={true} />
        </span>
      ),
    };
  };
  const advised = service?.help_options_advised_with_price.map(createHelpOption) || [];
  const other = service?.help_options_with_price.map(createHelpOption) || [];

  // If escape is present we should add it as last option of "other"
  if (hasEscape) {
    other.push({
      value: 'equipment_provided_by_customer',
      title: String(translate(HELP_ICON_AND_TITLE_TRANSKEY.equipment_provided_by_customer.titleKey)),
      desc: <Translate id={HELP_ICON_AND_TITLE_TRANSKEY.equipment_provided_by_customer.explanationKey} />,
      suffix: <span className={'text-primary'}>{priceAsString({ price: { amount: 0 } })}</span>,
      prefix: HELP_ICON_AND_TITLE_TRANSKEY.equipment_provided_by_customer.icon,
    });
  }

  /**
   * Map the help values to a HelpOption
   * Add escape value
   */
  return {
    advised,
    other,
    hasEscape,
  };
};
